import React, {
  Context,
  createContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { globalHistory } from '@reach/router';
import {
  NotificationSeverity,
  NotificationVariant,
} from '../components/outage-map-notifications/OutageMapNotifications';

const defaultState = {
  isOpen: false,
  severity: 'info',
  variant: 'filled',
  message: '',
};

const OutageMapNotificationContext: Context<any> = createContext(defaultState);

function getTimestamp() {
  return Date.now().valueOf();
}

// Close after 3 seconds
const TIMEOUT = 3000;
// Prevent history from closing when the last open was within this time
const CLOSE_ON_HISTORY_THRESHOLD = 500;

const OutageMapNotificationContextProvider = (props: any) => {
  const [state, setState] = useState(defaultState);
  const lastOpen = useRef<number | null>(null);
  const value = {
    state,
    setState: (context: any) => {
      if (context.isOpen) {
        lastOpen.current = getTimestamp();
        setState({
          variant: 'filled',
          ...context,
        });
      } else {
        lastOpen.current = null;
        setState(st => ({ ...st, variant: 'filled', ...context }));
      }
    },
  };

  useMemo(() => {
    globalHistory.listen(() => {
      if (
        lastOpen.current !== null &&
        getTimestamp() - lastOpen.current > CLOSE_ON_HISTORY_THRESHOLD
      ) {
        lastOpen.current = null;
        setState(defaultState);
      }
    });
  }, []);

  const timer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (state.isOpen) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(
        () => setState(st => ({ ...st, isOpen: false })),
        TIMEOUT,
      );
    }
  }, [state.message, state.isOpen]);

  return (
    <OutageMapNotificationContext.Provider value={value}>
      {props.children}
    </OutageMapNotificationContext.Provider>
  );
};

const OutageMapNotificationsContextConsumer =
  OutageMapNotificationContext.Consumer;

export {
  OutageMapNotificationContext,
  OutageMapNotificationContextProvider,
  OutageMapNotificationsContextConsumer,
};
